import React, { useCallback, useEffect, useMemo } from 'react'

import { Fragment } from 'react'

import getFormStatus from '@/Form/Functions/getFormStatus'

import Checkbox from '@/Form/V2/PullState/Checkbox'
import Input    from '@/Form/V2/Input'

import { num, formatNum, isTrue } from '@root/utility'

import FormsStore from '@Store/Forms'

const header =
[
	{ value: 'cb', label: '' },
	{ value: 'text_number', label: 'Facture', cn_label: 'Avoir' },
	{ value: 'order_number', label: 'Commande' },
	{ value: 'date', label: 'Date' },
	{ value: 'total_price', label: 'Facturé', cn_label: 'Montant' },
	{ value: 'paid_amount', label: 'Déjà réglé' },
	{ value: 'remaining_unpaid', label: 'Reste à régler', cn_label: 'Solde' },
	{ value: 'affected', label: 'Montant attribué' },
	{ value: 'remaining', label: 'Restant' }
];

const cell_class = 'c-pickable-bills__cell PickableBill-';

const { moment } = window;

const value_hooks =
{
	date: val => moment(val).format('DD/MM/YYYY'),
	total_price: val => formatNum(val, '0,0[.]00') + ' €',
	remaining_unpaid: val => formatNum(val, '0,0[.]00') + ' €',
	paid_amount: val => formatNum(val, '0,0[.]00') + ' €'
};

export default function PickableBills ({ bills, ...props })
{
	const to_attribute = FormsStore.useState(function (store)
	{
		return num(
			num(store[props.form]?.total_to_affect) + num(store[props.form]?.amount)
		);
	});

	const remaining_to_pay = useMemo(function ()
	{
		return bills.reduce(function (to_pay, bill)
		{
			return num(to_pay + num(bill.total_price) - num(bill.paid_amount));
		},
		0);
	},
	[bills]);

	const to_pay = useMemo(function ()
	{
		return bills.reduce(function (to_pay, bill)
		{
			return num(to_pay + num(bill.total_price));
		},
		0);
	},
	[bills]);

	const paid_amount = useMemo(function ()
	{
		return bills.reduce(function (paid_amount, bill)
		{
			return num(paid_amount + num(bill.paid_amount));
		},
		0);
	},
	[bills]);

	const has_selected = FormsStore.useState(function (store)
	{
		return Object.keys(store[props.form] || { })
		.some(key => key.startsWith('bills[Z') && isTrue(store[props.form][key]))
	});

	const header_cells = header.map(function (opt)
	{
		return (
			<div className={ cell_class + opt.value }
				key={ opt.value }>
				{
					props.type === 'credit_note'
					? opt.cn_label || opt.label
					: opt.label
				}
			</div>
		);
	});

	const bills_list = (bills || []).map(function (bill)
	{
		const cells = header.map(function (opt)
		{
			const class_name = cell_class + opt.value;

			return (
				<BillCell key={ opt.value + bill.number } form={ props.form }
					className={ class_name }
					name={ opt.value }
					bill={ bill }
					status={ props.status }
				/>
			);
		});

		return (
			<div className="c-pickable-bills__row" key={ bill.number }>
				{ cells }
			</div>
		);
	});

	const attributeAll = useCallback(function (e)
	{
		FormsStore.update(function (store)
		{
			store[props.form] ||= { };

			let remaining = num(to_attribute);

			bills.forEach(function (bill)
			{
			// on ajout Z parce que le parser back croit que '10' est un bool
				const cb_name = 'bills[Z' + bill.number + ']';

				if (!isTrue(store[props.form][cb_name])) return;

			// on ajout Z parce que le parser back croit que '10' est un bool
				const name = 'amounts[Z' + bill.number + ']';

				const value = remaining > 0 === false
				? ''
				: num(remaining) > num(bill.total_price) - num(bill.paid_amount)
				? String(num(num(bill.total_price) - num(bill.paid_amount)))
				: String(num(remaining))

				remaining = num(remaining - value);

				store[props.form][name] = value;
			});
		});
	},
	[to_attribute, props.form, bills]);

	const checkAll = useCallback(function (e)
	{
		FormsStore.update(function (store)
		{
			store[props.form] ||= { };

			bills.forEach(function (bill)
			{
			// on ajout Z parce que le parser back croit que '10' est un bool
				const cb_name = 'bills[Z' + bill.number + ']';

				store[props.form][cb_name] = true;
			});
		});
	},
	[bills]);

	return (
		<>
			<div className="c-pickable-bills">

				<div className="c-pickable-bills__header">
					{ header_cells }
				</div>

				<div className="c-pickable-bills__list">
					{ bills_list }
				</div>

				<div className="c-pickable-bills__totals">
					<Totals to_attribute={ to_attribute }
						bills={ bills }
						form={ props.form }
						attributeAll={ attributeAll }
						has_selected={ has_selected }
						to_pay={ to_pay }
						paid_amount={ paid_amount }
						remaining_to_pay={ remaining_to_pay }
						checkAll={ checkAll }
					/>
				</div>

			</div>

			{
				to_attribute > 0 === false
				? getFormStatus({ warning: 'Saisissez un montant' })
				: has_selected ? undefined
				: getFormStatus({ warning: 'Sélectionnez une facture' })
			}
		</>
	);
}

function Remaining ({ form, number, total, paid_amount })
{
// on ajout Z parce que le parser back croit que '10' est un bool
	const name = 'amounts[Z' + number + ']';

	const affected = FormsStore.useState(function (store)
	{
		return num(store[form]?.[name]);
	});

	const remaining = num(num(total) - num(paid_amount) - num(affected));

	let class_name = 'c-pickable-bills__remaining';

	if (remaining < 0)
	{
		class_name += ' has-error';
	}

	return (
		<div className={ class_name }>
			{ formatNum(remaining, '0,0[.]00') }
			{ remaining < 0 ? ' ' : '' }
			{ ' €' }
		</div>
	);
}

function Totals ({ form, paid_amount, to_pay, remaining_to_pay, to_attribute,
	attributeAll, checkAll, has_selected })
{
	const affected = FormsStore.useState(function (store)
	{
		return Object.keys(store[form] || { })
		.filter(key => key.startsWith('amounts[Z'))
		.reduce(function (affected, key)
		{
			return num(affected + num(store[form][key]))
		},
		0);
	});

	const remaining = num(to_attribute - affected);

	const totals_cells = header.map(function (opt)
	{
		let class_name = cell_class + opt.value;

		let value = undefined;

		if (opt.value === 'number')
		{
			value = to_attribute > 0 ?
			(
				<button type="button" onClick={ checkAll }
					className="c-btn c-btn--in-cell">
					Tout sélectionner
				</button>
			)
			: undefined;
		}
		else if (opt.value === 'affected')
		{
			let class_name = 'c-pickable-bills__remaining';

			if (remaining < 0) class_name += ' has-error';
			if (remaining === 0) class_name += ' has-green-highlight';
			if (remaining > 0) class_name += ' has-blue-highlight';

			value =
			(
				<Fragment>
					<div className={ class_name }>
						{ formatNum(remaining, '0,0[.]00') + ' €' }
					</div>
					{
						has_selected && to_attribute > 0 ?
						(
							<button type="button" onClick={ attributeAll }
								className="c-btn c-btn--in-cell">
								Attribuer automatiquement
							</button>
						)
						: undefined
					}
				</Fragment>
			);
		}
		else if (opt.value === 'total_price')
		{
			value = formatNum(to_pay, '0,0[.]00') + ' €';
		}
		else if (opt.value === 'paid_amount')
		{
			value = formatNum(paid_amount, '0,0[.]00') + ' €';
		}
		else if (opt.value === 'remaining_unpaid')
		{
			value = formatNum(remaining_to_pay, '0,0[.]00') + ' €';
		}
		else if (opt.value === 'remaining')
		{
			value = formatNum(
				num(num(to_pay) - num(paid_amount) - num(affected)), '0,0[.]00'
			) + ' €';
		}

		return (
			<div className={ class_name }
				key={ opt.value }>
				{ value }
			</div>
		);
	});

	return totals_cells;
}

function BillCell ({ form, name, bill, ...props })
{
// on ajout Z parce que le parser back croit que '10' est un bool
	const cb_name = 'bills[Z' + bill.number + ']';

	const enabled = FormsStore.useState(function (store)
	{
		return isTrue(store[form]?.[cb_name]);
	});

	useEffect(function ()
	{
		if (!enabled)
		{
		// on ajout Z parce que le parser back croit que '10' est un bool
			const input_name = 'amounts[Z' + bill.number + ']';

			FormsStore.update(function (store)
			{
				store[form] ||= { };

				store[form][input_name] = '';
			});
		}
	},
	[enabled, form, bill.number]);

	const setMax = useCallback(function (e)
	{
	// on ajout Z parce que le parser back croit que '10' est un bool
		const input_name = 'amounts[Z' + bill.number + ']';

		FormsStore.update(function (store)
		{
			store[form] ||= { };

			store[form][input_name] = String(
				num(num(bill.total_price) - num(bill.paid_amount))
			);
		});
	},
	[]);

	if (name === 'cb')
	{
		return (
			<div className={ props.className } key="cb">
				<Checkbox name={ cb_name } bemit={ ['in-cell'] }
					form={ form }
				/>
			</div>
		);
	}

	if (name === 'affected')
	{
	// on ajout Z parce que le parser back croit que '10' est un bool
		const input_name = 'amounts[Z' + bill.number + ']';

		return (
			<div className={ props.className } key="affected">
				{
					!enabled ? undefined :
					(
						<Input name={ input_name }
							form={ form }
							bemit={ ['in-cell'] }
							error={ props.status?.fields?.[input_name]?.error }
							safe-number
						/>
					)
				}
				{
					!enabled ? undefined :
					(
						<button type="button" onClick={ setMax }
							className="c-btn c-btn--in-cell">
							Max
						</button>
					)
				}
			</div>
		);
	}

	if (name === 'remaining')
	{
		return (
			<div className={ props.className } key="remaining">
				<Remaining number={ bill.number }
					total={ bill.total_price }
					paid_amount={ bill.paid_amount }
					form={ form }
				/>
			</div>
		);
	}

	return (
		<div className={ props.className }>
			{
				value_hooks[name]
				? value_hooks[name](bill[name], name, bill)
				: bill[name]
			}
		</div>
	);
}